import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSite } from "@/hooks/use-site";
import type { BlogPostSummary } from "@/lib/blog";
import { localizeUrl } from "@/lib/site";
import { BlogPostCard } from "@/components/blog/BlogPostCard";

// `posts` comes from the home route's loader (a server function under the
// hood — see @/lib/latest-posts) rather than calling listPosts() here
// directly, so ./blog's yaml + eager glob of every locale's Markdown doesn't
// end up in this (home page) client bundle.
export function LatestNews({ posts }: { posts: BlogPostSummary[] }) {
  const { site, messages, href } = useSite();

  // Auto-hide when there isn't enough fresh content to fill the row cleanly.
  if (posts.length < 3) return null;

  return (
    <section id="news" className="relative border-t border-border/60 section-tint py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
              {messages.newsEyebrow}
            </div>
            <h2 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl">
              {messages.newsTitle} <span className="text-gradient">{messages.newsTitleAccent}</span>
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{messages.newsDescription}</p>
          </div>
          <a
            href={localizeUrl("/blog", site.locale, href)}
            className="group inline-flex shrink-0 items-center gap-2 text-sm font-medium text-primary"
          >
            {messages.newsViewAll}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {posts.map((post) => (
            <BlogPostCard
              key={post.slug}
              post={post}
              href={localizeUrl(`/blog/${post.slug}`, site.locale, href)}
              messages={messages}
            />
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <Button asChild size="lg" variant="outline" className="group h-12 rounded-lg px-7">
            <a href={localizeUrl("/blog", site.locale, href)}>
              {messages.newsViewAll}
              <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
